import { useState } from "react";
import { ArrowDown, ArrowUp, GitCompareArrows, Minus, TriangleAlert } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "~/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { EvalRunStatus } from "~/pages/manifold-nextgen/types/eval-run";
import {
  JUDGE_MODEL_KEYS,
  type EvalRun,
  type PartialEvalCase,
  type PartialEvalCaseResult,
  type PartialEvalRun,
} from "~/services/evals";
import { EvalTable } from "./EvalTable";
import {
  COMPARISON_FILTER_ORDER,
  COMPARISON_VERDICT_CONFIG,
  ComparisonVerdict,
} from "./constants/comparison";
import { ALL_FILTER, RUN_STATUS_CONFIG } from "./constants/runs";
import { OutcomeBadge } from "./primitives/badges";
import { ScoreValue } from "./primitives/figures";
import { formatEvalTimestamp } from "./primitives/format";
import { type ComparisonRow, buildComparison } from "./selectors/comparison";
import { parseApiTimestamp, parseTriggeredBy } from "./selectors/core";
import {
  sortRunsChronologically,
  useEvalRun,
  useEvalRunResults,
  type RunAgentLabels,
} from "./useEvalApi";

interface RunComparisonCardProps {
  /** The full run: the confound check reads `judge_config` and `run_parameters`. */
  run: EvalRun;
  results: PartialEvalCaseResult[];
  cases: PartialEvalCase[];
  /** Every run on the customer, so a baseline can be any earlier one. */
  allRuns: PartialEvalRun[];
  /** Resolved by the page, which already reads every candidate baseline's agent. */
  agentLabels: RunAgentLabels;
}

/**
 * What changed against a baseline run, case by case. A mean score holds still
 * while one case collapses and another recovers, so an aggregate reports "no
 * change" for the most important event in the window.
 *
 * The baseline defaults to the run immediately before this one; any earlier run
 * can be picked instead, which is how an agent or model swap gets evaluated.
 *
 * Two runs are rarely a clean A/B, so what differs is stated above the table. A
 * score that moved because the rubric or the eval moved is not the agent getting
 * worse, and a comparison that cannot say which is which gets ignored.
 */
export const RunComparisonCard = ({
  run,
  results,
  cases,
  allRuns,
  agentLabels,
}: RunComparisonCardProps) => {
  // Only earlier runs: comparing forwards would report a regression that has not
  // happened yet, with the sign inverted.
  const earlierRuns = sortRunsChronologically(
    allRuns.filter(
      (candidate) =>
        candidate.id !== run.id &&
        parseApiTimestamp(candidate.started_at) < parseApiTimestamp(run.started_at)
    )
  ).reverse();

  // The most recent earlier run that *succeeded*: a failed run posted no results,
  // so defaulting to one opens this card reporting every case as new. Failed and
  // running runs stay selectable, labelled with their status.
  const defaultBaseline =
    earlierRuns.find((candidate) => candidate.status === EvalRunStatus.SUCCEEDED) ?? earlierRuns[0];
  // Null until a reviewer picks one, and resolved during render rather than
  // seeded into state: the run list arrives after the first paint, so a state
  // initializer would hold the empty default and leave the card unset.
  const [chosenBaselineId, setChosenBaselineId] = useState<string | null>(null);
  const baselineId = chosenBaselineId ?? defaultBaseline?.id ?? "";
  const [verdictFilter, setVerdictFilter] = useState<string>(ALL_FILTER);
  const { data: baselinePage, isLoading } = useEvalRunResults(baselineId || null);
  // The full baseline, not the list shape: `judge_config` and `run_parameters`
  // are what make the difference between the two runs reportable, and only the
  // single-run route carries them.
  const { data: baselineRun } = useEvalRun(baselineId || null);

  if (earlierRuns.length === 0) {
    return null;
  }

  const comparison = buildComparison(cases, results, baselinePage?.items ?? []);
  const rows =
    verdictFilter === ALL_FILTER
      ? comparison.rows
      : comparison.rows.filter((row) => row.verdict === verdictFilter);
  // Regressions first, then by how far the score fell: the reviewer's reading
  // order, and it holds whichever chip is active.
  const sorted = [...rows].sort(
    (a, b) =>
      VERDICT_RANK[a.verdict] - VERDICT_RANK[b.verdict] || (a.scoreDelta ?? 0) - (b.scoreDelta ?? 0)
  );

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 pb-4">
        <div>
          <CardTitle className="flex items-center gap-2 text-base font-semibold">
            <GitCompareArrows className="size-4 text-muted-foreground" aria-hidden />
            What changed
          </CardTitle>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Every case&apos;s score in this run against a baseline run.
          </p>
        </div>
        <Select value={baselineId} onValueChange={setChosenBaselineId}>
          <SelectTrigger className="w-[260px]" aria-label="Baseline run to compare against">
            <SelectValue placeholder="Pick a baseline run…" />
          </SelectTrigger>
          <SelectContent>
            {earlierRuns.map((candidate) => {
              const { trigger } = parseTriggeredBy(candidate.triggered_by);
              // A run that did not succeed is named by its status rather than its
              // trigger: that is the fact that decides whether it can serve as a
              // baseline at all.
              const qualifier =
                candidate.status === EvalRunStatus.SUCCEEDED
                  ? candidate.id === defaultBaseline?.id
                    ? "previous successful run"
                    : trigger
                  : RUN_STATUS_CONFIG[candidate.status].label.toLowerCase();
              return (
                <SelectItem key={candidate.id} value={candidate.id}>
                  {formatEvalTimestamp(parseApiTimestamp(candidate.started_at))}
                  {qualifier ? ` · ${qualifier}` : ""}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {isLoading && !baselinePage ? (
          <Skeleton className="h-48 rounded-lg" />
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <VerdictChip
                label="All cases"
                count={comparison.rows.length}
                isActive={verdictFilter === ALL_FILTER}
                onSelect={() => setVerdictFilter(ALL_FILTER)}
              />
              {COMPARISON_FILTER_ORDER.map((verdict) => (
                <VerdictChip
                  key={verdict}
                  label={COMPARISON_VERDICT_CONFIG[verdict].label}
                  count={comparison.counts[verdict]}
                  toneClass={COMPARISON_VERDICT_CONFIG[verdict].toneClass}
                  isActive={verdictFilter === verdict}
                  onSelect={() => setVerdictFilter(verdict)}
                />
              ))}
            </div>

            <ConfoundNotice run={run} baseline={baselineRun ?? null} agentLabels={agentLabels} />

            {(baselinePage?.items.length ?? 0) === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                The baseline run recorded no results, so there is nothing to compare against. Pick a
                run that succeeded.
              </p>
            ) : sorted.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                No cases in this group.
              </p>
            ) : (
              <EvalTable>
                <TableHeader>
                  <TableRow>
                    <TableHead>Case</TableHead>
                    <TableHead className="text-right">Baseline</TableHead>
                    <TableHead className="text-right">This run</TableHead>
                    <TableHead className="text-right">Change</TableHead>
                    <TableHead>Outcome</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map((row) => (
                    <ComparisonTableRow key={row.caseId} row={row} />
                  ))}
                </TableBody>
              </EvalTable>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * What differs between the two runs besides the agent's behaviour. Each line is a
 * field the API keeps so this question can be answered: `agent_id` and the
 * written-back `model_name` say what was measured, `judge_config` says what
 * measured it, and `iterations` says how many samples each score averages.
 *
 * Absent when the two runs agree on all of them, since a banner that is always
 * there stops being read.
 */
const ConfoundNotice = ({
  run,
  baseline,
  agentLabels,
}: {
  run: EvalRun;
  baseline: EvalRun | null;
  agentLabels: RunAgentLabels;
}) => {
  if (!baseline) {
    return null;
  }

  const differences: string[] = [];
  if (run.agent_id !== baseline.agent_id) {
    differences.push(
      `a different agent (${agentLabels.agentLabel(baseline.agent_id) ?? "none recorded"} to ${
        agentLabels.agentLabel(run.agent_id) ?? "none recorded"
      })`
    );
  }
  if (run.model_name !== baseline.model_name) {
    differences.push(
      `a different orchestrator model (${baseline.model_name ?? "unrecorded"} to ${run.model_name ?? "unrecorded"})`
    );
  }
  const judgeChanges = JUDGE_MODEL_KEYS.filter(
    (entry) => run.judge_config?.[entry.key] !== baseline.judge_config?.[entry.key]
  );
  if (judgeChanges.length > 0) {
    differences.push(
      `a different ${judgeChanges.map((entry) => entry.label.toLowerCase()).join(" and ")}`
    );
  }
  if (run.run_parameters.iterations !== baseline.run_parameters.iterations) {
    differences.push(
      `${run.run_parameters.iterations ?? "an unrecorded number of"} iterations per case where the baseline used ${baseline.run_parameters.iterations ?? "an unrecorded number"}`
    );
  }
  // The tag selectors, last because it explains something different from the
  // others: a scope change does not bias a score, it decides which cases exist
  // on both sides at all. It is the usual reason a comparison fills up with
  // "New" and "Dropped" rows, and without saying so those read as a defect.
  if (!sameTags(run.tags, baseline.tags)) {
    differences.push(
      `a different case selector (${describeTags(baseline.tags)} to ${describeTags(run.tags)})`
    );
  }

  if (differences.length === 0) {
    return null;
  }

  return (
    <p className="mb-3 flex items-start gap-2 rounded-md border border-orange-300 bg-orange-50 p-3 text-xs text-orange-900">
      <TriangleAlert className="mt-px size-3.5 shrink-0" aria-hidden />
      <span>
        These runs are not a clean comparison: this one used {joinClauses(differences)}. Read a
        score change as the sum of that and the agent, not as the agent alone.
      </span>
    </p>
  );
};

/** Tag selectors match when they hold the same tags, whatever order they arrive in. */
function sameTags(a: string[], b: string[]): boolean {
  return a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|");
}

/** A run's tag selector in prose. No tags is not "none" — it selected every case. */
function describeTags(tags: string[]): string {
  return tags.length === 0 ? "all cases" : tags.join(" + ");
}

/** `a, b and c` — an Oxford-comma-free list, for prose inside a sentence. */
function joinClauses(clauses: string[]): string {
  if (clauses.length <= 1) {
    return clauses.join("");
  }
  return `${clauses.slice(0, -1).join(", ")} and ${clauses[clauses.length - 1]}`;
}

/** Reading order for the grouped table: what needs work, then what improved. */
const VERDICT_RANK: Record<ComparisonVerdict, number> = {
  [ComparisonVerdict.REGRESSED]: 0,
  [ComparisonVerdict.ADDED]: 1,
  [ComparisonVerdict.REMOVED]: 2,
  [ComparisonVerdict.IMPROVED]: 3,
  [ComparisonVerdict.UNCHANGED]: 4,
};

const ComparisonTableRow = ({ row }: { row: ComparisonRow }) => (
  <TableRow>
    <TableCell className="max-w-[340px]">
      <span
        className="block max-w-full truncate font-mono text-xs text-foreground"
        title={row.caseName}
      >
        {row.caseName}
      </span>
      {row.versionDrift && <VersionDriftNote drift={row.versionDrift} />}
    </TableCell>
    <TableCell className="text-right text-xs">
      <ScoreValue score={row.baselineScore} />
    </TableCell>
    <TableCell className="text-right text-xs">
      <ScoreValue score={row.currentScore} />
    </TableCell>
    <TableCell className="text-right">
      <ScoreDelta row={row} />
    </TableCell>
    <TableCell>
      {/* Only shown when it actually moved: repeating the same badge twice on
          every row would make the column noise rather than a signal. */}
      {row.currentOutcome === row.baselineOutcome ? (
        <OutcomeBadge outcome={row.currentOutcome} />
      ) : (
        <span className="flex flex-wrap items-center gap-1.5">
          <OutcomeBadge outcome={row.baselineOutcome} />
          <ArrowRightGlyph />
          <OutcomeBadge outcome={row.currentOutcome} />
        </span>
      )}
    </TableCell>
  </TableRow>
);

/**
 * The case was edited between the two runs, so its delta is not only the agent. A
 * per-row note, since the rows carrying it are exactly the ones not to trust. The
 * bump is scoring-relevant by construction: the API leaves cosmetic edits alone.
 */
const VersionDriftNote = ({ drift }: { drift: { baseline: number; current: number } }) => (
  <Tooltip delayDuration={200}>
    <TooltipTrigger className="mt-0.5 flex cursor-help items-center gap-1 text-[11px] text-orange-700">
      <TriangleAlert className="size-3" aria-hidden />
      case edited between runs
    </TooltipTrigger>
    <TooltipContent side="bottom" className="max-w-xs">
      <p className="text-xs">
        The baseline ran v{drift.baseline} of this case and this run ran v{drift.current}. The
        version only moves when something scoring-relevant changes — the question, the expectations,
        the datasets or a threshold — so part of this delta is the eval, not the agent.
      </p>
    </TooltipContent>
  </Tooltip>
);

const ArrowRightGlyph = () => (
  <span className="text-xs text-muted-foreground" aria-label="became">
    →
  </span>
);

/**
 * The score change, signed. A case present in only one of the two runs shows the
 * reason rather than a dash: "New" and "Dropped" are findings about the tag
 * selector, and a dash sends a reviewer looking for a scoring bug.
 */
const ScoreDelta = ({ row }: { row: ComparisonRow }) => {
  const config = COMPARISON_VERDICT_CONFIG[row.verdict];
  if (row.scoreDelta == null) {
    return <span className={cn("text-xs", config.toneClass)}>{config.label}</span>;
  }
  if (row.verdict === ComparisonVerdict.UNCHANGED) {
    return (
      <span className="flex items-center justify-end gap-0.5 text-xs text-muted-foreground">
        <Minus className="size-3" aria-hidden />
        <span className="sr-only">unchanged</span>
      </span>
    );
  }
  const isUp = row.scoreDelta > 0;
  const Icon = isUp ? ArrowUp : ArrowDown;
  return (
    <span
      className={cn(
        "flex items-center justify-end gap-0.5 text-xs font-medium tabular-nums",
        config.toneClass
      )}
    >
      <Icon className="size-3" aria-hidden />
      {Math.abs(row.scoreDelta).toFixed(2)}
    </span>
  );
};

/**
 * A count that is also the filter for it. An empty group is itself the answer to
 * "did anything regress", so a zero chip stays visible but is not selectable.
 */
const VerdictChip = ({
  label,
  count,
  toneClass,
  isActive,
  onSelect,
}: {
  label: string;
  count: number;
  toneClass?: string;
  isActive: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    disabled={count === 0}
    aria-pressed={isActive}
    className={cn(
      "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors",
      isActive
        ? "border-primary bg-primary/10 text-foreground"
        : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground",
      count === 0 &&
        "cursor-not-allowed opacity-50 hover:border-border hover:text-muted-foreground",
      count > 0 && !isActive && "cursor-pointer"
    )}
  >
    <span className={cn("tabular-nums", count > 0 && toneClass)}>{count}</span>
    {label}
  </button>
);
